import {
  DataGridPro,
  getGridStringOperators,
  GridColDef,
  GridRenderCellParams,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarFilterButton,
  useGridApiRef,
} from "@mui/x-data-grid-pro";
import { Link } from "react-router-dom";
import { LinearProgress, Stack, styled } from "@mui/material";
import useServerSideDataGridParams from "hooks/useServerSideDataGridParams";
import useLogisticsPartners from "hooks/useLogisticsPartners";
import { LogisticsPartner } from "types/logisticsPartner";

const StyledDataGrid = styled(DataGridPro)(({ theme }) => ({
  "& .MuiDataGrid-row:hover": {
    backgroundColor: theme.palette.mode == "dark" ? theme.palette.primary.dark : theme.palette.primary.light,
  },
}));

export default function PartnersDataGrid() {
  const defaultPageSize = 25;
  const usePartnersState = useLogisticsPartners(defaultPageSize);
  const { logisticsPartners, count, isLoading } = usePartnersState;
  const apiRef = useGridApiRef();

  const CustomToolbar = () => {
    return (
      <GridToolbarContainer>
        <GridToolbarColumnsButton {...({} as any)} />
        <GridToolbarFilterButton {...({} as any)} />
        <GridToolbarDensitySelector {...({} as any)} />
      </GridToolbarContainer>
    );
  };

  const columns: GridColDef[] = [
    {
      field: "name",
      headerName: "Name",
      width: 300,
      sortable: false,
      filterOperators: getGridStringOperators().filter((op) => op.value === "contains"),
      renderCell: (params: GridRenderCellParams<LogisticsPartner>) => {
        return <Link to={`/logistics/partners/${params.row.uuid}`}>{params.row.name}</Link>;
      },
    },
    {
      field: "type",
      headerName: "Type",
      sortable: false,
      width: 200,
      filterOperators: getGridStringOperators().filter((op) => op.value === "contains"),
    },
    {
      field: "googleDriveLink",
      headerName: "Google Drive",
      sortable: false,
      filterable: false,
      width: 200,
      renderCell: (params: GridRenderCellParams<LogisticsPartner>) => {
        return (
          <a href={params.row.googleDriveLink} target="_blank">
            {params.row.googleDriveLink ? "View Drive" : ""}
          </a>
        );
      },
    },
  ];

  const pageState = useServerSideDataGridParams(logisticsPartners);

  return (
    <StyledDataGrid
      rows={logisticsPartners}
      columns={columns}
      {...pageState}
      pageSize={pageState.pageSize || defaultPageSize}
      rowsPerPageOptions={[5, 10, 25, 100]}
      rowCount={count}
      filterMode="server"
      apiRef={apiRef}
      disableSelectionOnClick
      checkboxSelection={false}
      checkboxSelectionVisibleOnly={false}
      loading={isLoading}
      components={{
        LoadingOverlay: LinearProgress,
        Toolbar: CustomToolbar,
        NoResultsOverlay: () => (
          <Stack height="100%" alignItems="center" justifyContent="center">
            No Partners matching your filters
          </Stack>
        ),
      }}
      componentsProps={{ filterPanel: { sx: { minHeight: "200px" } } }}
    />
  );
}
